import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { XIcon } from 'lucide-react';
import { Button } from '@rocco/components/button';
import { Spinner } from '@rocco/components/spinner';
import { AlertDialog } from '@rocco/ui/dialog/components/AlertDialog';
import { TOAST_CLOSE_BUTTON } from '~/utils/toast';
import { getResizedImageURL } from '~/modules/images';
import { useConversation } from '../../../context';
import type { DirectConversation } from '../../../types';
import { validateChatThumbnailUploadFile } from '../../../utils/validation';
import { useUpdateConversation } from '../../../hooks/use-update-conversation';

/**
 * The thumbnail upload component in the chat box conversation settings "Settings" tab.
 * @see {@link ChatBoxSettingsSettings}
 */
export function ChatBoxSettingsThumbnail({
    className,
    conversation,
    isDisabled: isDisabledProp = false,
}: {
    className?: string;
    conversation: DirectConversation;
    isDisabled?: boolean;
}) {
    const [file, setFile] = useState<File | null>(null);
    const { professionalID } = useConversation();
    const { isSubmitting, updateConversationThumbnail } = useUpdateConversation(
        {
            conversationID: Number(conversation.ID),
            professionalID,
            onUpdateThumbnailSuccess: () => {
                setFile(null);
            },
        },
    );
    const isDisabled = isDisabledProp || isSubmitting;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const resizedThumbnailUrl = useMemo(() => {
        return conversation.Thumbnail?.Link
            ? getResizedImageURL(conversation.Thumbnail.Link, 200, 200)
            : undefined;
    }, [conversation.Thumbnail?.Link]);
    const filePreviewUrl = useMemo(() => {
        return file ? URL.createObjectURL(file) : null;
    }, [file]);

    function onFileChange(files: FileList | null) {
        if (!files?.length) {
            return;
        }
        const newFile = files[0]!;
        const validationResult = validateChatThumbnailUploadFile(newFile);
        if (validationResult.valid) {
            setFile(newFile);
            updateConversationThumbnail(newFile);
        } else {
            toast.error(validationResult.message, TOAST_CLOSE_BUTTON);
        }
    }

    // Clean up the object URL to prevent memory leaks.
    useEffect(() => {
        return () => {
            if (filePreviewUrl) {
                URL.revokeObjectURL(filePreviewUrl);
            }
        };
    }, [filePreviewUrl]);

    return (
        <div className={className}>
            {resizedThumbnailUrl || file ? (
                <div className="relative aspect-square size-[140px]">
                    <button
                        type="button"
                        className="group relative size-full rounded-full bg-white disabled:pointer-events-none disabled:opacity-50"
                        disabled={isDisabled}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <img
                            src={filePreviewUrl ?? resizedThumbnailUrl}
                            alt="Chat thumbnail"
                            className="size-full rounded-full object-cover"
                        />
                        <div className="absolute left-1/2 top-1/2 translate-x-[-50%] translate-y-[-50%] rounded-[8px] bg-white p-[8px] text-[14px] font-medium leading-tight text-black opacity-0 transition-opacity duration-100 group-hover:opacity-100">
                            Upload
                        </div>
                    </button>
                    <AlertDialog
                        title="Remove thumbnail"
                        description="Are you sure you want to remove this thumbnail?"
                        onConfirm={() => updateConversationThumbnail(null)}
                        trigger={
                            <button
                                type="button"
                                className="absolute right-[-4px] top-[-4px] z-1 flex size-[36px] items-center justify-center rounded-full border border-black/20 bg-white hover:bg-gray-200 disabled:pointer-events-none disabled:opacity-50"
                                disabled={isDisabled}
                            >
                                <XIcon className="size-[24px]" />
                            </button>
                        }
                    />

                    {isSubmitting && (
                        <div className="absolute left-1/2 top-1/2 translate-x-[-50%] translate-y-[-50%]">
                            <Spinner className="size-[32px]" />
                        </div>
                    )}
                </div>
            ) : (
                <Button
                    type="button"
                    className="px-[12px] py-[8px] text-[14px] leading-tight"
                    color="dark"
                    weight="normal"
                    disabled={isDisabled}
                    onClick={() => fileInputRef.current?.click()}
                >
                    Upload thumbnail
                </Button>
            )}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                disabled={isDisabled}
                onChange={(e) => onFileChange(e.target.files)}
            />
        </div>
    );
}
